/**
 * ZenMarkup Privacy Analytics — Line Chart core module.
 * Canvas 2D line chart with metric-tab switching, entrance animation,
 * and debounced resize. Delegates drawing to line-chart-draw,
 * X-axis to line-chart-axis, hover/tooltip to line-chart-hover.
 */

import * as lineChartDraw from './line-chart-draw.js';
import * as lineChartAxis from './line-chart-axis.js';
import * as lineChartHover from './line-chart-hover.js';
import * as animate from './animate.js';

let canvas, ctx, timeseries = [], activeSeries = 'visitors';
let resizeTimer = null, hoverIdx = -1;
let tabsBound = false;
let hasAnimated = false, activeAnim = null;
const LINE_COLOR = '#2563EB';
const HOVER_DOT_RADIUS = 5, HOVER_DOT_BORDER = 2;
var PAD = { top: 20, right: 16, bottom: 44, left: 24 };
var PAD_LEFT_MIN = 24;

function updatePad() {
	PAD.right = window.innerWidth < 600 ? 20 : 16;
}

/**
 * Edge margin matches card content padding so Y-axis labels
 * align with widget titles ("Devices", "Peak Hours", etc.).
 *
 * @return {number} 18px desktop, 14px mobile (card padding + 2px visual correction)
 */
function edgeMargin() {
	return window.innerWidth < 600 ? 14 : 18;
}

/**
 * Measure widest Y-axis label and set PAD.left accordingly.
 * Single formula for all screen sizes (Chart.js / uPlot pattern).
 * Research: docs/research/chart-y-axis-sizing.md
 *
 * Layout: [edgeMargin][label text][LABEL_GAP]|grid line at PAD.left
 *
 * @param {number[]} values  Data values for the current series.
 */
function fitAxisLeft(values) {
	if (!ctx) return;
	ctx.font = lineChartDraw.AXIS_FONT;
	var maxVal = lineChartDraw.niceMax(Math.max.apply(null, values), activeSeries);
	var steps = 5;
	var maxW = 0;
	for (var i = 0; i <= steps; i++) {
		var label = lineChartDraw.formatYValue((maxVal / steps) * i, activeSeries);
		var w = ctx.measureText(label).width;
		if (w > maxW) maxW = w;
	}
	PAD.left = Math.max(PAD_LEFT_MIN, Math.ceil(maxW) + lineChartDraw.LABEL_GAP + edgeMargin());
}

function niceMax(max) {
	return lineChartDraw.niceMax(max, activeSeries);
}

function formatYValue(val) {
	return lineChartDraw.formatYValue(val, activeSeries);
}

/** @param {Array} data Timeseries array. */
export function render(data) {
	timeseries = data || [];
	canvas = document.querySelector('[data-el="line-canvas"]');
	if (!canvas) return;
	ctx = canvas.getContext('2d');
	bindMetricTabs();
	lineChartHover.createTooltip();
	bindEvents();
	if (!hasAnimated) {
		animateEntrance();
	} else {
		draw();
	}
}

/** Bind click events on metric tab buttons. */
function bindMetricTabs() {
	if (tabsBound) return;
	tabsBound = true;
	const tabs = document.querySelectorAll('[data-el="metric-card"]');
	tabs.forEach(function (tab) {
		tab.addEventListener('click', function () {
			const metric = tab.getAttribute('data-metric');
			if (!metric || metric === activeSeries) return;
			activeSeries = metric;
			tabs.forEach(function (t) {
				const isActive = t.getAttribute('data-metric') === metric;
				t.classList.toggle('zmpa-metric-card-active', isActive);
				t.setAttribute('aria-selected', isActive ? 'true' : 'false');
			});
			draw();
		});
	});
}

function bindEvents() {
	if (canvas.dataset.bound) return;
	canvas.dataset.bound = '1';

	var hoverOpts = {
		canvas: canvas,
		timeseries: timeseries,
		PAD: PAD,
		activeSeries: activeSeries,
		niceMax: niceMax,
		formatYValue: formatYValue,
		getHoverIdx: function () { return hoverIdx; },
		setHoverIdx: function (idx) { hoverIdx = idx; draw(); }
	};

	canvas.addEventListener('mousemove', function (e) {
		hoverOpts.timeseries = timeseries;
		hoverOpts.activeSeries = activeSeries;
		lineChartHover.onMouseMove(e, hoverOpts);
	});
	canvas.addEventListener('mouseleave', function () {
		lineChartHover.onMouseLeave(function (idx) {
			hoverIdx = idx;
			draw();
		});
	});

	let touchHideTimer = 0;
	function handleTouch(e) {
		if (!e.touches.length) return;
		e.preventDefault();
		clearTimeout(touchHideTimer);
		const touch = e.touches[0];
		const fake = { clientX: touch.clientX, clientY: touch.clientY };
		hoverOpts.timeseries = timeseries;
		hoverOpts.activeSeries = activeSeries;
		lineChartHover.onMouseMove(fake, hoverOpts);
	}
	canvas.addEventListener('touchstart', handleTouch, { passive: false });
	canvas.addEventListener('touchmove', handleTouch, { passive: false });
	canvas.addEventListener('touchend', function () {
		touchHideTimer = setTimeout(function () {
			lineChartHover.onMouseLeave(function (idx) {
				hoverIdx = idx;
				draw();
			});
		}, 2000);
	});
	window.addEventListener('resize', function () {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(draw, 200);
	});
}

function draw(progress) {
	if (!canvas || !ctx) return;
	updatePad();
	const dpr = window.devicePixelRatio || 1;
	const w = Math.floor(canvas.parentElement.getBoundingClientRect().width);
	const h = 280;
	canvas.width = w * dpr;
	canvas.height = h * dpr;
	canvas.style.width = w + 'px';
	canvas.style.height = h + 'px';
	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	const values = timeseries.map(function (d) { return d[activeSeries]; });
	if (!values.length) {
		fitAxisLeft([0, 2, 4, 6, 8, 10]);
		drawEmptyState(w, h);
		return;
	}
	fitAxisLeft(values);
	drawGrid(w, h, values);
	drawLine(w, h, values, progress);
	drawXAxis(w, h);
	if (hoverIdx >= 0) {
		lineChartHover.drawOverlay({
			ctx: ctx, w: w, h: h, values: values,
			hoverIdx: hoverIdx, timeseries: timeseries, PAD: PAD,
			lineColor: LINE_COLOR, labelFont: lineChartDraw.LABEL_FONT,
			dotRadius: HOVER_DOT_RADIUS, dotBorder: HOVER_DOT_BORDER,
			niceMax: niceMax
		});
	}
}

function drawEmptyState(w, h) {
	lineChartDraw.drawEmptyState({
		ctx: ctx, w: w, h: h, PAD: PAD,
		activeSeries: activeSeries, drawXAxis: drawXAxis
	});
}

function drawGrid(w, h, values) {
	lineChartDraw.drawGrid({
		ctx: ctx, w: w, h: h, values: values,
		PAD: PAD, activeSeries: activeSeries
	});
}

function drawLine(w, h, values, progress) {
	lineChartDraw.drawLine({
		ctx: ctx, w: w, h: h, values: values,
		PAD: PAD, activeSeries: activeSeries,
		progress: progress, lineColor: LINE_COLOR
	});
}

function drawXAxis(w, h) {
	lineChartAxis.drawXAxis({
		ctx: ctx, timeseries: timeseries, w: w, h: h, PAD: PAD,
		hoverIdx: hoverIdx, labelFont: lineChartDraw.LABEL_FONT, labelColor: '#64748B'
	});
}

function animateEntrance() {
	if (activeAnim) {
		activeAnim.cancel();
	}
	activeAnim = animate.run(function (progress) {
		draw(progress);
		if (progress >= 1) {
			activeAnim = null;
			hasAnimated = true;
		}
	}, 1600, animate.easeOutQuint);
}

export { draw as redraw };
